import { Decimal } from 'decimal.js'
import { RefObject } from 'react'

import {
  GetCardsTransactionsWithPurchaseApplicationUnlinked200ResponseInnerMergedCardTransactionTransactionRecordListInner,
  GetCardsTransactionsWithPurchaseApplicationUnlinked200ResponseInner,
} from '@src/api/generated/user-api'
import { TAX } from '@src/consts'

const ONE_MILLION = new Decimal(1_000_000)

export function showFx(
  record: GetCardsTransactionsWithPurchaseApplicationUnlinked200ResponseInnerMergedCardTransactionTransactionRecordListInner
) {
  // Not shown in the case of Japanese yen.
  if (record.currencyCode === 'JPY') return false

  // In accordance with the following Visa specifications,
  // the display of foreign currency may not match the Japanese yen when amount is 1 yen.
  // So hide amountFx when amount is 1 yen to let not user confuse.
  //
  // With respect to the processing of authorization transactions in the smallest possible unit of currency
  // that occur due to overseas use  Currently, Visa's Base-I system has a special treatment for Japanese issuers
  // that is valid for This is the case when the amount in Field 4 is in the smallest local currency unit
  // (e.g., 1 USD 1 EUR). If the amount in Field 4 is in the smallest local currency unit (e.g., USD 1, EUR 1, etc.),
  // it will be calculated as follows Field 6, Amount,
  // Cardholder Billing with no exchange rate conversion to 1 yen We have included the
  // Clearing across the month could make the difference 1 yen.
  // In that case the record shouldn't be determined to be an authorization transaction.
  // The logic below assumes that the authorization transaction record is
  // either Visa_Auth or Visa_AuthCancelByNoClearing.
  if (
    Math.abs(record.amount) === 1 &&
    (record.transactionDetailType === 'PURCHASE' ||
      record.transactionDetailType === 'CANCEL')
  )
    return false

  return true
}

export function showFxForMergedTxn(
  amountFx: number,
  transactionRecordList: GetCardsTransactionsWithPurchaseApplicationUnlinked200ResponseInner['mergedCardTransaction']['transactionRecordList']
) {
  const noNeedToShowForAll = transactionRecordList.every(
    (record) => !showFx(record)
  )
  if (noNeedToShowForAll) return false

  // No need to display it when it's zero.
  if (amountFx === 0) return false

  return true
}

/**
 * @description Move the cursor to the last character of the input.
 */
export function moveToCursorToLastCharacter(
  inputRef: RefObject<HTMLInputElement>
) {
  inputRef.current?.setSelectionRange(999, 999)
}

export function excludeTax(amount: number) {
  return new Decimal(amount).div(TAX).toNumber()
}

// https://www.nta.go.jp/taxes/shiraberu/taxanswer/gensen/2792.htm
export function calcManuscriptOrLecture(amount: number) {
  const TAX_RATE_FOR_MANUSCRIPT_OR_LECTURE_UPPER_ONE_MILLION_YEN = new Decimal(
    0.2042
  )
  const FIXED_AMOUNT_FOR_MANUSCRIPT_OR_LECTURE_UPPER_ONE_MILLION_YEN =
    new Decimal(102100)
  const TAX_RATE_FOR_MANUSCRIPT_OR_LECTURE_LOWER_OR_EQUAL_ONE_MILLION_YEN =
    new Decimal(0.1021)

  const amountDecimal = new Decimal(amount)

  if (amountDecimal.gt(ONE_MILLION)) {
    const t = amountDecimal.minus(ONE_MILLION)
    const result = t.times(
      TAX_RATE_FOR_MANUSCRIPT_OR_LECTURE_UPPER_ONE_MILLION_YEN
    )
    return result
      .plus(FIXED_AMOUNT_FOR_MANUSCRIPT_OR_LECTURE_UPPER_ONE_MILLION_YEN)
      .floor()
      .toNumber()
  }
  return amountDecimal
    .times(TAX_RATE_FOR_MANUSCRIPT_OR_LECTURE_LOWER_OR_EQUAL_ONE_MILLION_YEN)
    .floor()
    .toNumber()
}

export function calcLawyerOrCPA(amount: number) {
  const TAX_RATE_FOR_LAWYER_OR_CPA_UPPER_ONE_MILLION_YEN = new Decimal(0.2042)
  const FIXED_AMOUNT_FOR_LAWYER_OR_CPA_UPPER_ONE_MILLION_YEN = new Decimal(
    102100
  )
  const TAX_RATE_FOR_LAWYER_OR_CPA_LOWER_OR_EQUAL_ONE_MILLION_YEN = new Decimal(
    0.1021
  )

  const amountDecimal = new Decimal(amount)

  if (amountDecimal.gt(ONE_MILLION)) {
    const result = amountDecimal.minus(ONE_MILLION)
    return result
      .times(TAX_RATE_FOR_LAWYER_OR_CPA_UPPER_ONE_MILLION_YEN)
      .plus(FIXED_AMOUNT_FOR_LAWYER_OR_CPA_UPPER_ONE_MILLION_YEN)
      .floor()
      .toNumber()
  } else {
    return amountDecimal
      .times(TAX_RATE_FOR_LAWYER_OR_CPA_LOWER_OR_EQUAL_ONE_MILLION_YEN)
      .floor()
      .toNumber()
  }
}

export function calcJudicialScrivener(amount: number) {
  const EXEMPT_AMOUNT_FOR_JUDICIAL_SCRIVENER = new Decimal(10000)
  const TAX_RATE_FOR_JUDICIAL_SCRIVENER = new Decimal(0.1021)

  const amountDecimal = new Decimal(amount)

  const result = amountDecimal
    .minus(EXEMPT_AMOUNT_FOR_JUDICIAL_SCRIVENER)
    .times(TAX_RATE_FOR_JUDICIAL_SCRIVENER)

  if (result.isNegative()) {
    return 0
  }

  return result.floor().toNumber()
}

export function calcDeductibleAmount({
  totalAmount,
  withholdingAmount,
}: {
  totalAmount: number
  withholdingAmount: number
}) {
  return totalAmount - withholdingAmount
}
